// @givmo/compliance — the panel's way into a prepared form (CCM-R1-DOCS, web half; slice 2 §4,
// frames 30 and 49).
//
// Before anything is prepared: "Prepare the form" beside "Record this filing", for a person who
// can write, on a filing whose form Givmo prepares (a served adapter for the rule), with the quiet
// line under them. It opens the case for the year the next date names (or the filing made once)
// when none is open, starts the form there, and opens the workbench.
//
// Once preparation has started: "This year’s filing" ("The filing" for a filing made once) with its
// four fixed lines — Prepared, Signed, Sent to Maryland, Maryland’s confirmation — each read from
// its own record, so a signature never reads as sent or confirmed (DESIGN-NOTE-2 §4), then
// "Open the form". The line under the heading names the form as it prints itself, with its number
// where that differs (frames 60, 67; CCM-R1-DOCS-PRINTED-WEB), and a year-keyed filing's year
// (frame 49).
//
// Once the form is signed (CCM-R1-SUBMIT-WEB; slice 3 §4, frames 60, 67 and 83), in Givmo or by a
// copy signed outside it (CCM-R1-SIGN-OUTSIDE-SEND-B, frame 110), the case's
// submission read gives "Sent to Maryland" its send's state — sending, sent, not sent, outcome
// uncertain, recorded as not sent — and leads the buttons with **Review and send** where Givmo can
// email the filing, or **Check what happened** while a send's outcome is uncertain. Sending is its
// own dark door: closed, the read answers nothing and the panel reads as before.
//
// Where Givmo won't email it (a fee; no email channel), a writer is shown **How to send it** (frame
// 78, CCM-R1-SUBMIT-HANDOFF); once the organization recorded what it sent, "Sent to Maryland" reads
// "Nov 20, 2026, by mail · recorded by Maya Solberg" and no send button leads (frame 81).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, testIdProp, toast } from '@givmo/design-system';
import { useCompliance } from './compliance-context';
import {
  givmoSignatureStands,
  recorderOf,
  signedOutsideLine,
} from './compliance-signed-outside-model';
import { useSignedOutside } from './signed-outside';
import { serverMessage, type ComplianceObligation } from './compliance-decode';
import type { RenewalCycle } from './compliance-filings-decode';
import type { NextDate } from './compliance-filings-model';
import {
  adapterForRule,
  decodeFormPreparation,
  decodeOpenedFilingCase,
  decodeSigningRequestSigners,
  extractFilingCases,
  extractFormAdapters,
  extractPreparationSummaries,
  extractSigningRequests,
  type SigningRequestRow,
} from './compliance-preparation-decode';
import {
  OPEN_THE_FORM,
  PREPARE_LINE,
  PREPARE_THE_FORM,
  THE_FILING,
  THIS_YEARS_FILING,
  caseForTarget,
  filingSubtitle,
  ledgerLabels,
  ledgerValues,
  openCaseBody,
  preparationTargetFor,
  type PreparationTarget,
} from './compliance-preparation-model';
import {
  caseSigningRequestsQueryKey,
  casePreparationsQueryKey,
  filingCasesQueryKey,
  formAdaptersQueryKey,
  formPreparationQueryKey,
  getCaseSubmission,
  listAllCasePreparations,
  listAllCaseSigningRequests,
  listAllFilingCases,
  listAllFormAdapters,
  signingRequestQueryKey,
  submissionQueryKey,
} from './compliance-queries';
import { COMPLIANCE_ROUTES, FILING_YEAR_SEARCH_KEY } from './compliance-routes';
import { decodeSubmission, type SubmissionHandoff } from './compliance-submission-decode';
import { decodeDeskCase, deskHolds } from './compliance-desk-model';
import {
  newestHandoff,
  panelSendAction,
  sendActionLabel,
  sentLedgerValue,
  type PanelSendAction,
} from './compliance-submission-model';

const PREFIX = 'compliance-filing-panel-preparation';

/** Each send button's test id suffix (the e2e contract's). */
const SEND_ACTION_TEST_IDS = { review: 'send', check: 'check', handoff: 'how-to-send' } as const;

/** The request the Signed line reads: the newest open one, else the newest completed one. */
function ledgerRequest(rows: readonly SigningRequestRow[]): SigningRequestRow | undefined {
  const newestFirst = [...rows].sort((a, b) =>
    (b.createdAt ?? '').localeCompare(a.createdAt ?? ''),
  );
  return (
    newestFirst.find((row) => row.state === 'open') ??
    newestFirst.find((row) => row.state === 'completed')
  );
}

export interface PanelPreparation {
  /** Whether the filing's form is one Givmo prepares, and the panel may offer it. */
  offered: boolean;
  /** Whether preparation has started for the year the next date names. */
  started: boolean;
  /** Still reading: nothing is drawn until every read has answered. */
  loading: boolean;
  prepare: () => void;
  preparing: boolean;
  heading: string;
  /**
   * The form and the period it reports: "Annual Update of Registration Form · fiscal year ended June
   * 30, 2026"; "Registration Statement for Charitable Organizations · Form COR-92".
   */
  subtitle?: string;
  labels: readonly string[];
  values: readonly string[];
  /**
   * Which send button leads the row, once the form is signed: Review and send, Check what happened,
   * or How to send it.
   */
  sendAction?: PanelSendAction;
  /** What the organization recorded it sent itself, newest (its year's tile lists the files). */
  handoff?: SubmissionHandoff;
  /** The year the case is for, where it is a year (the tile the record joins without a date). */
  cycleId?: string;
}

/** Every read the panel's preparation needs, keyed as the workbench keys them. */
export function usePanelPreparation(
  obligation: ComplianceObligation | undefined,
  next: NextDate | undefined,
  onOpen: (to: string) => void,
): PanelPreparation {
  const { orgRef, isReady, moduleStanding, operations, personName } = useCompliance();
  const queryClient = useQueryClient();
  const ref = isReady && moduleStanding === 'available' ? orgRef : null;
  const target = next !== undefined ? preparationTargetFor(next) : undefined;

  const adapters = useQuery({
    queryKey: formAdaptersQueryKey(ref),
    queryFn: () => listAllFormAdapters(operations, ref ?? ''),
    enabled: ref !== null && obligation !== undefined,
  });
  const adapter =
    obligation !== undefined
      ? adapterForRule(
          extractFormAdapters(adapters.data),
          obligation.jurisdictionCode,
          obligation.ruleId,
        )
      : undefined;
  const cases = useQuery({
    queryKey: filingCasesQueryKey(ref, obligation?.obligationId),
    queryFn: () => listAllFilingCases(operations, ref ?? '', obligation?.obligationId ?? ''),
    enabled: ref !== null && adapter !== undefined && target !== undefined,
  });
  const openCase =
    target !== undefined ? caseForTarget(extractFilingCases(cases.data), target) : undefined;
  const preparations = useQuery({
    queryKey: casePreparationsQueryKey(ref, openCase?.caseId),
    queryFn: () => listAllCasePreparations(operations, ref ?? '', openCase?.caseId ?? ''),
    enabled: ref !== null && openCase !== undefined,
  });
  const summary = extractPreparationSummaries(preparations.data)
    .filter((candidate) => candidate.adapterKey === adapter?.adapterKey)
    .at(-1);
  const preparationRead = useQuery({
    queryKey: formPreparationQueryKey(ref, summary?.preparationId),
    queryFn: () => operations.getFormPreparation(ref ?? '', summary?.preparationId ?? ''),
    enabled: ref !== null && summary !== undefined,
  });
  const requests = useQuery({
    queryKey: caseSigningRequestsQueryKey(ref, openCase?.caseId),
    queryFn: () => listAllCaseSigningRequests(operations, ref ?? '', openCase?.caseId ?? ''),
    enabled: ref !== null && summary !== undefined && openCase !== undefined,
  });
  const request = ledgerRequest(extractSigningRequests(requests.data));
  const requestRead = useQuery({
    queryKey: signingRequestQueryKey(ref, request?.requestId),
    queryFn: () => operations.getSigningRequest(ref ?? '', request?.requestId ?? ''),
    enabled: ref !== null && request !== undefined,
  });
  const preparation = decodeFormPreparation(preparationRead.data);
  const signers = decodeSigningRequestSigners(requestRead.data);
  // CCM-R1-SIGN-OUTSIDE-SEND (P4): a completed request is the form's signature only while it
  // executes the form's newest version, as the server reads the form's execution. A read of the
  // form from before the request completed never unsigns it (F1).
  const givmoSigned = givmoSignatureStands(
    request?.state,
    preparation?.currentExecution,
    signers?.documentVersionIds,
    preparation?.latestVersion?.versionId,
  );
  // CCM-R1-SIGN-OUTSIDE-B (frame 89): a copy signed outside Givmo of the version Givmo last
  // recorded reads on the Signed line (W15), while nothing was signed in Givmo.
  const { outside, pending: copyPending } = useSignedOutside(preparation, { withPages: false });
  // Once signed, in Givmo or outside it (CCM-R1-SIGN-OUTSIDE-SEND-B, frame 110), the case's
  // submission: what "Sent to Maryland" says and which button leads.
  const signedCase = givmoSigned || outside !== undefined ? openCase?.caseId : undefined;
  const submissionRead = useQuery({
    queryKey: submissionQueryKey(ref, signedCase),
    queryFn: () => getCaseSubmission(operations, ref ?? '', signedCase ?? ''),
    enabled: ref !== null && signedCase !== undefined,
  });
  const submission = decodeSubmission(submissionRead.data);

  const prepare = useMutation({
    mutationFn: async () => {
      if (obligation === undefined || target === undefined) throw new Error('');
      let caseId = openCase?.caseId;
      if (caseId === undefined) {
        const opened = decodeOpenedFilingCase(
          await operations.openFilingCase(ref ?? '', obligation.obligationId, openCaseBody(target)),
        );
        if (opened === undefined) throw new Error('');
        caseId = opened.caseId;
      }
      await operations.startFormPreparation(ref ?? '', caseId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['compliance'] });
      if (obligation !== undefined) onOpen(COMPLIANCE_ROUTES.filingForm(obligation.obligationId));
    },
    onError: (cause) =>
      toast.error(serverMessage(cause, 'We couldn’t record that. Please try again.')),
  });

  const loading =
    obligation !== undefined &&
    (adapters.isPending ||
      (adapter !== undefined && target !== undefined && cases.isPending) ||
      (openCase !== undefined && preparations.isPending) ||
      (summary !== undefined && (preparationRead.isPending || requests.isPending)) ||
      (request !== undefined && requestRead.isPending) ||
      (signedCase !== undefined && submissionRead.isPending) ||
      copyPending);

  const jurisdiction = obligation?.jurisdictionName || obligation?.jurisdictionCode || '';
  const ledger =
    obligation !== undefined
      ? ledgerValues(
          {
            preparation,
            // A completed request that signed an earlier version is not the form's (P4).
            request: request?.state === 'completed' && !givmoSigned ? undefined : request,
            signerNames: (signers?.signerUserIds ?? []).flatMap((userId) => {
              const name = personName?.(userId);
              return name !== undefined ? [name] : [];
            }),
            signedAt: signers?.completedAt,
            cycle: target?.kind === 'renewal' ? target.cycle : undefined,
            obligation,
          },
          personName,
        )
      : [];
  // Frame 83: the case's newest send says what "Sent to Maryland" reads, where one was made.
  const sent = submission !== undefined ? sentLedgerValue(submission, personName) : undefined;
  // Frame 89: Signed reads the copy signed outside Givmo, where one stands.
  const signedOutside =
    outside !== undefined && !givmoSigned
      ? signedOutsideLine(recorderOf(outside.copy, personName), outside.copy.createdAt)
      : undefined;
  const values = ledger.map((value, index) =>
    index === 2 && sent !== undefined
      ? sent
      : index === 1 && signedOutside !== undefined
        ? signedOutside
        : value,
  );
  // CCM-R1-DESK-WEB (Q12): while Givmo's desk has the filing, no way of the organization's own to
  // send it, only Check what happened (review-143's F4).
  const sendAction =
    submission !== undefined
      ? panelSendAction(submission, deskHolds(decodeDeskCase(submissionRead.data)))
      : undefined;
  const handoff = submission !== undefined ? newestHandoff(submission) : undefined;

  return {
    offered: adapter !== undefined && target !== undefined,
    started: summary !== undefined,
    loading,
    prepare: () => prepare.mutate(),
    preparing: prepare.isPending,
    heading: target?.kind === 'renewal' ? THIS_YEARS_FILING : THE_FILING,
    ...(adapter !== undefined
      ? {
          subtitle: filingSubtitle(
            {
              formNumber: adapter.formNumber,
              ...(preparation?.printedTitle !== undefined
                ? { printedTitle: preparation.printedTitle }
                : {}),
            },
            target?.kind === 'renewal' && !target.cycle.cycleKey.startsWith('standing')
              ? target.cycle.periodEnd
              : undefined,
          ),
        }
      : {}),
    labels: ledgerLabels(jurisdiction),
    values,
    ...(sendAction !== undefined ? { sendAction } : {}),
    ...(handoff !== undefined ? { handoff } : {}),
    ...(target?.kind === 'renewal' ? { cycleId: target.cycle.cycleId } : {}),
  };
}

/** "Prepare the form", as the action row's first button (frame 30). */
export function PrepareButton({ preparation }: { preparation: PanelPreparation }) {
  return (
    <Button
      appearance="saas"
      tone="secondary"
      size="sm"
      loading={preparation.preparing}
      onClick={preparation.prepare}
      testId={`${PREFIX}-prepare`}
    >
      {PREPARE_THE_FORM}
    </Button>
  );
}

/** The quiet line under the action row's two buttons (frame 30). */
export function PrepareLine() {
  return (
    <p className="text-s12 leading-h15 text-text-tertiary" {...testIdProp(`${PREFIX}-line`)}>
      {PREPARE_LINE}
    </p>
  );
}

/**
 * The four lines and "Open the form" (frame 49), led by the send's button once signed, in Givmo
 * (frame 60) or outside it (frame 110).
 */
export function PreparedFiling({
  preparation,
  onOpen,
  onSend,
}: {
  preparation: PanelPreparation;
  onOpen: () => void;
  /** The send review, followed in place; absent, no send button shows. */
  onSend?: () => void;
}) {
  return (
    <section className="flex flex-col gap-10" {...testIdProp(PREFIX)}>
      <div className="flex flex-col gap-6">
        <h2 className="text-s14 font-w600 text-text-strong">{preparation.heading}</h2>
        {preparation.subtitle !== undefined && (
          <p className="text-s13 text-text-secondary" {...testIdProp(`${PREFIX}-subtitle`)}>
            {preparation.subtitle}
          </p>
        )}
      </div>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-20 gap-y-8 rounded-lg border border-border-subtle px-16 py-12 text-s13">
        {preparation.labels.map((label, index) => (
          <div key={label} className="contents">
            <dt className="text-text-tertiary">{label}</dt>
            <dd className="text-text" {...testIdProp(`${PREFIX}-line-${index + 1}`)}>
              {preparation.values[index]}
            </dd>
          </div>
        ))}
      </dl>
      <div className="flex flex-wrap gap-8">
        {preparation.sendAction !== undefined && onSend !== undefined && (
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={onSend}
            testId={`${PREFIX}-${SEND_ACTION_TEST_IDS[preparation.sendAction]}`}
          >
            {sendActionLabel(preparation.sendAction)}
          </Button>
        )}
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          onClick={onOpen}
          testId={`${PREFIX}-open`}
        >
          {OPEN_THE_FORM}
        </Button>
      </div>
    </section>
  );
}

export interface YearPreparation {
  /** Prepare a year's form: its case opened where none is, the form started unless it is, then opened. */
  prepareYear: (cycle: RenewalCycle) => void;
  /** The year being prepared, while its writes run. */
  preparingYear?: string;
}

/**
 * CCM-R1-LATE-FILINGS (D-CCM-62, frames 1a and 2b): "Prepare the form" on a year read overdue. As the
 * panel's own way in does for the next date's year, the year's own case is opened where none is, and
 * its form started there unless it already is; then the workbench opens on that year (`?cycle=`), so
 * any year is prepared, signed and sent from its own year.
 */
export function useYearPreparation(
  obligation: ComplianceObligation | undefined,
  onOpen: (to: string, search: Readonly<Record<string, string>>) => void,
): YearPreparation {
  const { orgRef, isReady, moduleStanding, operations } = useCompliance();
  const queryClient = useQueryClient();
  const ref = isReady && moduleStanding === 'available' ? orgRef : null;
  const adapters = useQuery({
    queryKey: formAdaptersQueryKey(ref),
    queryFn: () => listAllFormAdapters(operations, ref ?? ''),
    enabled: ref !== null && obligation !== undefined,
  });
  const adapter =
    obligation !== undefined
      ? adapterForRule(
          extractFormAdapters(adapters.data),
          obligation.jurisdictionCode,
          obligation.ruleId,
        )
      : undefined;
  const prepare = useMutation({
    mutationFn: async (cycle: RenewalCycle) => {
      if (obligation === undefined || adapter === undefined || ref === null) throw new Error('');
      const target: PreparationTarget = { kind: 'renewal', cycle };
      const cases = extractFilingCases(
        await listAllFilingCases(operations, ref, obligation.obligationId),
      );
      let caseId = caseForTarget(cases, target)?.caseId;
      if (caseId === undefined) {
        const opened = decodeOpenedFilingCase(
          await operations.openFilingCase(ref, obligation.obligationId, openCaseBody(target)),
        );
        if (opened === undefined) throw new Error('');
        caseId = opened.caseId;
      }
      const started = extractPreparationSummaries(
        await listAllCasePreparations(operations, ref, caseId),
      ).some((summary) => summary.adapterKey === adapter.adapterKey);
      if (!started) await operations.startFormPreparation(ref, caseId);
    },
    onSuccess: (_, cycle) => {
      void queryClient.invalidateQueries({ queryKey: ['compliance'] });
      if (obligation !== undefined)
        onOpen(COMPLIANCE_ROUTES.filingForm(obligation.obligationId), {
          [FILING_YEAR_SEARCH_KEY]: cycle.cycleId,
        });
    },
    onError: (cause) =>
      toast.error(serverMessage(cause, 'We couldn’t record that. Please try again.')),
  });
  return {
    prepareYear: (cycle) => prepare.mutate(cycle),
    ...(prepare.isPending && prepare.variables !== undefined
      ? { preparingYear: prepare.variables.cycleId }
      : {}),
  };
}
